import { getInstance as getCurrentUserCfg } from 'OK/CurrentUserCfg';
import { StatLogger } from 'OK/StatLogger';
import { createCacheStorage, IStorage } from 'cache-storage-lib';

export interface IUncensoredImage {
    id: string;
}

export type IUncensorImageListener = (image: IUncensoredImage) => unknown;

export interface IUncensorImageParams<TImage extends ISensitiveImage = ISensitiveImage> {
    /**
     * Регистрируемое изображение или его идентификатор.
     */
    image: TImage | number | string;
    /**
     * Надо ли отправлять в систему статистики сведения об открытии изображения для просмотра.
     * Значение по умолчанию - `true`.
     */
    log?: boolean;
}

export interface ISensitiveImage {
    id?: number | string;
    isSensitive?: boolean;
}

interface IUncensorImageChannelMessage {
    image: string;
    user: string;
}

const LOG_PREFIX = 'photo.blur.';
const WHITE_LIST_STORAGE_KEY = 'imageBlurWhiteList';
const WHITE_LIST_STORAGE_NAMESPACE = 'blur-sensitive';
const WHITE_LIST_SIZE = 100;

class ImageSensitiveService {

    private channel: BroadcastChannel | null = null;

    private imageBlurWhiteList: string[];

    private uncensorImageListenerList: IUncensorImageListener[] = [];

    private statLogger: StatLogger;

    private userId: string;

    private whiteListSaveTimeoutId: number | null = null;

    private readonly newStorage: IStorage | null = null;

    private readonly newStorageEnabled;

    private readonly whiteListStorageKey: string

    constructor(newStorageIsEnabled: boolean) {
        this.statLogger = new StatLogger(LOG_PREFIX);
        this.userId = getCurrentUserCfg().getUserId() || '';
        this.newStorageEnabled = newStorageIsEnabled;
        this.whiteListStorageKey = this.getWhiteListStorageKey();

        if (this.newStorageEnabled) {
            this.newStorage = this.createStorage();
            this.imageBlurWhiteList = [];
        } else {
            this.imageBlurWhiteList = this.loadImageBlurWhiteList();
        }

        if (typeof window.BroadcastChannel === 'function') {
            try {
                this.channel = new window.BroadcastChannel('ImageSensitiveService');
            } catch (e) {
                this.statLogger.error('failedNewBroadcastChannel', e);
            }
            if (this.channel) {
                this.channel.onmessage = (event) => {
                    const uncensorData: IUncensorImageChannelMessage | undefined = event.data.uncensor;
                    const imageId = uncensorData?.image;
                    if (imageId && uncensorData.user === this.userId && this.imageBlurWhiteList.indexOf(imageId) < 0) {
                        this.imageBlurWhiteList.push(imageId);
                        this.runUncensorImageListener(imageId);
                    }
                };
            }
        }
    }

    init() {
        if (!this.newStorageEnabled) {
            return;
        }

        this.loadImageBlurWhiteListAsync().then(whiteListIds => {
                for (const imageId of whiteListIds) {
                    if (this.imageBlurWhiteList.indexOf(imageId) !== -1) {
                        continue;
                    }

                    this.imageBlurWhiteList.push(imageId);
                    this.runUncensorImageListener(imageId);
                }
            }
        ).catch(e => this.statLogger.error('failedStorageGet', e));
    }

    private getWhiteListStorageKey() {
        return this.userId
            ? `${WHITE_LIST_STORAGE_KEY}_${this.userId}`
            : WHITE_LIST_STORAGE_KEY;
    }

    private loadImageBlurWhiteList(): string[] {
        const storage = this.getLocalStorage();
        if (storage == null) {
            return [];
        }

        let imageBlurWhiteList: string[] = [];
        let whiteList = storage.getItem(this.getWhiteListStorageKey());
        if (whiteList) {
            imageBlurWhiteList = JSON.parse(whiteList);
        }

        return imageBlurWhiteList;
    }

    private loadImageBlurWhiteListAsync(): Promise<string[]> {
        if (this.newStorage == null) {
            return Promise.resolve([]);
        }

        return this.newStorage.get<string[]>(this.whiteListStorageKey)
            .then(list => list == null ? [] : list)
            .catch(e => {
                this.statLogger.error('whiteListGetIsFailed', e);

                return [];
            });
    }

    private storeImageBlurWhiteListAsync = () => {
        if (this.newStorage == null) {
            return;
        }

        try {
            const task = this.imageBlurWhiteList.length
                ? this.newStorage.put(this.whiteListStorageKey, this.imageBlurWhiteList.slice(-WHITE_LIST_SIZE))
                : this.newStorage.delete(this.whiteListStorageKey);

            task.catch(e => this.statLogger.error('failedStorageModification', e));
        } catch (e) {
            // No op
        }
    };

    private storeImageBlurWhiteList = () => {
        const storage = this.getLocalStorage();
        if (storage == null) {
            return;
        }

        try {
            if (this.imageBlurWhiteList.length) {
                storage.setItem(this.getWhiteListStorageKey(), JSON.stringify(this.imageBlurWhiteList.slice(-WHITE_LIST_SIZE)));
            } else {
                storage.removeItem(this.getWhiteListStorageKey());
            }
        } catch (e) {
            // No op
        }
    };

    private saveImageBlurWhiteList() {
        if (this.newStorageEnabled) {
            this.storeImageBlurWhiteListAsync();
            return;
        }

        if (this.whiteListSaveTimeoutId !== null) {
            clearTimeout(this.whiteListSaveTimeoutId);
        }

        this.whiteListSaveTimeoutId = window.setTimeout(this.storeImageBlurWhiteList, 0);
    }

    private runUncensorImageListener(imageId: string) {
        const image: IUncensoredImage = {id: imageId};
        // Делаем копию списка обработчиков, чтобы выполнились все обработчики, даже если в процессе вызывается removeUncensorImageListener
        this.uncensorImageListenerList.slice(0).forEach((listener) => listener(image));
    }

    /**
     * Добавляет обработчик, вызываемый в случае, когда изображение становится доступным для просмотра.
     *
     * @param listener
     *      Добавляемый обработчик.
     */
    addUncensorImageListener(listener: IUncensorImageListener) {
        if (this.uncensorImageListenerList.indexOf(listener) < 0) {
            this.uncensorImageListenerList.push(listener);
        }
    }

    /**
     * Удаляет обработчик, вызываемый в случае, когда изображение становится доступным для просмотра.
     *
     * @param listener
     *      Добавляемый обработчик.
     */
    removeUncensorImageListener(listener: IUncensorImageListener) {
        const index = this.uncensorImageListenerList.indexOf(listener);
        if (index >= 0) {
            this.uncensorImageListenerList.splice(index, 1);
        }
    }

    /**
     * Проверяет, находится ли изображение в списке, исключающем его блюр.
     *
     * @param image
     *      Проверяемое изображение или его идентификатор.
     * @return
     *      `true`, если изображений находится в списке исключений по блюру.
     */
    isImageInBlurWhiteList<TImage extends ISensitiveImage = ISensitiveImage>(image: TImage | number | string): boolean {
        const id = String(
            typeof image === 'object'
                ? image.id
                : image
        );
        return this.imageBlurWhiteList.indexOf(id) > -1;
    }

    /**
     * Проверяет, отмечено ли указанное изображение, как возможно содержащее неприемлемый/оскорбительный контент.
     *
     * @param image
     *      Проверяемое изображение.
     * @return
     *      `true`, если изображение отмечено, как возможно содержащее неприемлемый/оскорбительный контент.
     */
    isImageSensitive<TImage extends ISensitiveImage = ISensitiveImage>(image: TImage): boolean {
        return Boolean(image.isSensitive);
    }

    /**
     * Проверяет, доступно ли изображение для просмотра.
     *
     * @param image
     *      Проверяемое изображение.
     * @return
     *      `true`, если изображение доступно для просмотра.
     */
    isUncensoredImage<TImage extends ISensitiveImage = ISensitiveImage>(image: TImage): boolean {
        return !this.isImageSensitive(image) || this.isImageInBlurWhiteList(image);
    }

    /**
     * Отправляет в систему статистики сведения о показе изображения, которое, возможно, содержит неприемлемый/оскорбительный контент.
     *
     * @param image
     *      Сведения об изображении или его идентификатор.
     */
    logImageShow<TImage extends ISensitiveImage = ISensitiveImage>(image: TImage | number | string) {
        if (typeof image !== 'object' || this.isImageSensitive(image)) {
            this.statLogger.success(this.isImageInBlurWhiteList(image) ? 'showWhitelistedPhoto' : 'showBlurredPhoto');
        }
    }

    /**
     * Регистрирует изображение, как доступное для просмотра.
     *
     * @param params
     *      Параметры операции.
     */
    uncensorImage<TImage extends ISensitiveImage = ISensitiveImage>(params: IUncensorImageParams<TImage>) {
        const {image, log = true} = params;
        const picture = typeof image === 'object'
            ? image
            : {
                id: image,
                isSensitive: true
            };
        const {id} = picture;
        if ((id || id === 0) && !this.isUncensoredImage(picture)) {
            const imageId = String(id);
            this.imageBlurWhiteList.push(imageId);
            this.saveImageBlurWhiteList();
            this.runUncensorImageListener(imageId);
            this.channel?.postMessage({
                uncensor: {
                    image: imageId,
                    user: this.userId
                }
            });
            if (log) {
                this.statLogger.success('uncensorClick');
            }
        }
    }

    private createStorage(): IStorage | null {
        try {
            const storage = createCacheStorage(WHITE_LIST_STORAGE_NAMESPACE);
            return storage && typeof storage === 'object' ? storage : null;
        } catch (e) {
            this.statLogger.error('accessDenied', e);
            return null;
        }
    }

    private getLocalStorage(): Storage | null {
        try {
            const storage = window.localStorage;
            return storage && typeof storage === 'object' ? storage : null;
        } catch (e) {
            this.statLogger.error('accessDenied', e);
            return null;
        }
    }
}

export type {ImageSensitiveService};

let imageSensitiveService: ImageSensitiveService;

export function getImageSensitiveService(newStorageIsEnabled: boolean) {
    if (!imageSensitiveService) {
        imageSensitiveService = new ImageSensitiveService(newStorageIsEnabled);
        imageSensitiveService.init();
    }

    return imageSensitiveService;
}
